"use client";

import React from "react";
import { Maximize2, Activity, Layers, ShieldCheck, CircleCheck, ArrowRight } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface ContentDict {
  // Technical guide intro
  badge: string;
  title: string;
  lead: string;

  // Section A: Surface vs Depth
  secATitle: string;
  secADesc: string;
  surfaceBadge: string;
  surfaceTitle: string;
  surfaceSubtitle: string;
  surfaceDesc: string;
  surfaceInterfaceTitle: string;
  surfaceInterfaceTag: string;
  surfaceCleanDischarge: string;
  surfacePoints: string[];
  depthBadge: string;
  depthTitle: string;
  depthSubtitle: string;
  depthDesc: string;
  depthMatrixTitle: string;
  depthMatrixTag: string;
  depthCoarseLayer: string;
  depthFineCore: string;
  depthSupportLayer: string;
  depthPoints: string[];

  // Section B: Nominal vs Absolute
  secBTitle: string;
  secBDesc: string;
  nominalTitle: string;
  nominalRating: string;
  nominalDesc: string;
  nominalNotes: string[];
  absoluteTitle: string;
  absoluteRating: string;
  absoluteDesc: string;
  absoluteNotes: string[];

  // Section C: Weaves
  secCTitle: string;
  secCDesc: string;
  weaveArchitectureLabel: string;
  weaveApplicationsLabel: string;
  weaveSpecLinkText: string;
  weaves: {
    id: string;
    name: string;
    structure: string;
    use: string;
    link?: string;
  }[];

  // Section D: Glossary
  secDTitle: string;
  secDDesc: string;
  glossaryRelevanceLabel: string;
  glossary: {
    term: string;
    symbol?: string;
    definition: string;
    significance: string;
  }[];
}
const copy: Record<Lang, ContentDict> = {
  en: {
    badge: "Technical Engineering Guide",
    title: "Filtration Principles & Mesh Mechanics",
    lead: "A technical reference on particle retention mechanisms, rating standards, wire weave architectures, and foundational calculation terminology for industrial mesh selection.",
    secATitle: "1. Surface Filtration vs. Depth Filtration",
    secADesc: "Retention mechanism determines media longevity, cake formation behavior, and regeneration methodology.",
    surfaceBadge: "2D Geometric Cutoff",
    surfaceTitle: "Surface Filtration",
    surfaceSubtitle: "Two-Dimensional Particle Cake Retention",
    surfaceDesc: "Particles are arrested strictly at the geometrical boundaries of the outer woven mesh surface. Once particles exceed the pore opening, a permeable cake layer forms, providing precise size cutoff and rapid backwashing capability.",
    surfaceInterfaceTitle: "FILTER INTERFACE",
    surfaceInterfaceTag: "WOVEN WIRE SURFACE",
    surfaceCleanDischarge: "↓↓ Clean Filtrate Discharge ↓↓",
    surfacePoints: ["Media configuration: Precision single-layer square weave or Dutch weave wire mesh.", "Mechanism: Geometric interception and aperture exclusion.", "Regeneration: Easily cleaned via reverse pulsed backwash or mechanical ultrasonic cleaning.", "Primary application: Straining, particle sizing, solid-liquid separation, high-solids continuous slurries."],
    depthBadge: "3D Tortuous Matrix",
    depthTitle: "Depth Filtration",
    depthSubtitle: "Three-Dimensional Tortuous Path Retention",
    depthDesc: "Fluid navigates through a multi-layered matrix or sintered wire composite. Contaminants penetrate into the core thickness and become entrapped across tortuous, branching micro-channels via impaction and entrapment.",
    depthMatrixTitle: "DEPTH MATRIX",
    depthMatrixTag: "SINTERED MULTILAYER COMPOSITE",
    depthCoarseLayer: "Coarse Protective Layer (100–200 μm)",
    depthFineCore: "Fine Sintered Filter Core (1–20 μm)",
    depthSupportLayer: "Heavy Structural Support Mesh (Rigid Base)",
    depthPoints: ["Media configuration: Multi-layer diffusion bonded sintered mesh or sintered metal laminates.", "Mechanism: Tortuous trajectory trapping, dynamic impingement, and multi-tier gradient retention.", "Regeneration: Extended dirty holding capacity; cleaned via chemical digestion, thermal burnoff, or high-pressure backflush.", "Primary application: Polymer melt extrusion, sub-micron critical gas/liquid filtration, hydraulic circuits."],
    secBTitle: "2. Nominal vs. Absolute Micron Rating",
    secBDesc: "Clarifying efficiency thresholds and laboratory verification procedures to prevent underspecifying process screens.",
    nominalTitle: "Nominal Micron Rating",
    nominalRating: "60% – 90% Efficiency Rating",
    nominalDesc: "An arbitrary commercial assessment indicating the ability of the mesh to arrest a predominant percentage of particulate solids of a stated dimension under standard test conditions.",
    nominalNotes: ["Retention efficiency: Typically rated between 60% and 90% particulate capture.", "Variable retention: Deformable or high-aspect-ratio particles may pass through under differential pressure surges.", "Testing baseline: Gravimetric single-pass efficiency methods with standard test dust.", "Intended use: Pre-filtration, coarse straining, protective security screening where complete particulate cutoff is not mandatory."],
    absoluteTitle: "Absolute Micron Rating",
    absoluteRating: "≥ 99.9% Retention Threshold",
    absoluteDesc: "The exact spherical glass bead diameter or glass particle dimension that will not pass through the largest recorded opening of the filter cloth under standardized pressure conditions.",
    absoluteNotes: ["Retention efficiency: 99.9% or higher (Beta ratio β ≥ 1000) at the verified micron rating.", "Rigid pore geometry: Structural stability prevents aperture enlargement under operating line pressure.", "Validation methods: Bubble Point Test (ASTM E128 / ISO 4003) and multi-pass multipoint challenge testing.", "Intended use: Critical pharmaceutical processes, aviation fuel filters, polymer spinnerets, semiconductor fluid systems."],
    secCTitle: "3. Common Industrial Weave Types",
    secCDesc: "Overview of basic and Dutch weave structures, structural dynamics, and process suitability.",
    weaveArchitectureLabel: "Weave Architecture",
    weaveApplicationsLabel: "Typical Industrial Applications",
    weaveSpecLinkText: "View technical specifications",
    weaves: [{
      id: "plain-weave",
      name: "Plain Weave Wire Mesh",
      structure: "Each warp wire crosses alternately over and under each shute (weft) wire. Warp and weft diameters are typically identical, forming standard square apertures.",
      use: "Widely used for particle classification, screening, liquid straining, and general architectural and protective filtration down to 25–38 microns.",
      link: "/PlainWeaveWireMeshPage"
    }, {
      id: "twilled-weave",
      name: "Twilled Weave Wire Mesh",
      structure: "Each weft wire passes alternately over two and under two warp wires, permitting heavier wire diameters relative to the aperture dimension.",
      use: "Selected when heavy structural wire is required at high mesh counts; suited for abrasive slurries, high mechanical loads, and demanding vibrating screen equipment.",
      link: "/TwilledWeaveWireMeshPage"
    }, {
      id: "plain-dutch",
      name: "Plain Dutch Weave (PDW)",
      structure: "Warp wires are spaced wider and thicker than the densely packed, thin weft wires. Produces firm wedge-shaped zero-light triangular apertures.",
      use: "Standard for high-pressure liquid and hydraulic fuel filtration, offering compact pore retention (down to 15–20 microns) with high mechanical shear stability.",
      link: "/PlainDutchWeavePage"
    }, {
      id: "twilled-dutch",
      name: "Twilled Dutch Weave (TDW)",
      structure: "Combines twill weaving pattern with tightly driven Dutch weft wires. Creates double-layer packed shute wires with a curved, tortuous microscopic flow path.",
      use: "Provides the finest filtration retention among standard woven cloths (down to 1–2 microns nominal/absolute); ideal for aerospace, chemical dosing, and fine polymer filters.",
      link: "/TwillDutchWeavePage"
    }, {
      id: "reverse-dutch",
      name: "Reverse Dutch Weave (RDW)",
      structure: "Inverted configuration featuring dense, heavier warp wires spaced closely alongside thin, flexible weft wires, yielding extremely high tensile strength lengthwise.",
      use: "Engineered specifically for continuous polymer melt screen changers, auto-screen filters, and high-pressure extrusion belts subject to intense longitudinal draw tension.",
      link: "/ReverseDutchWeavePage"
    }],
    secDTitle: "4. Technical Glossary & Core Engineering Metrics",
    secDDesc: "Essential definitions and hydraulic terms utilized during wire mesh specification and system sizing.",
    glossaryRelevanceLabel: "Engineering Relevance:",
    glossary: [{
      term: "Mesh Count",
      symbol: "N (Mesh)",
      definition: "The number of openings or wires per linear inch (25.4 mm), counted from the center of any wire.",
      significance: "Determines the fundamental weave density. In rectangular weaves, count is expressed as Warp Mesh × Weft Mesh."
    }, {
      term: "Aperture (Pore Opening)",
      symbol: "w",
      definition: "The clear distance between two adjacent parallel warp or weft wires, typically measured in microns (μm) or millimeters (mm).",
      significance: "The decisive geometric parameter for surface cutoff; calculated as w = (25.4 / N) - d."
    }, {
      term: "Wire Diameter",
      symbol: "d",
      definition: "The nominal gauge or thickness of the warp and weft wires prior to or following the weaving process.",
      significance: "Governs mechanical burst pressure, resistance to abrasive wear, and overall fabric flexibility."
    }, {
      term: "Open Area (Screen Transparency)",
      symbol: "Fo (%)",
      definition: "The percentage proportion of the overall screen surface comprised of free aperture openings versus solid wire area.",
      significance: "Directly determines fluid throughput velocity, pressure loss across the partition, and overall flow capacity."
    }, {
      term: "Micron Rating",
      symbol: "μm",
      definition: "A standardized linear unit of measure (1 micron = 0.001 mm) indicating the particle size exclusion capability of the filter medium.",
      significance: "Establishes retention classification. Differentiates between coarse bulk sizing (>100 μm) and microfiltration (1–100 μm)."
    }, {
      term: "Differential Pressure",
      symbol: "ΔP",
      definition: "The hydraulic pressure loss between the upstream inlet and downstream clean side of the filter screen element.",
      significance: "Critical metric for monitoring filter loading, cake accumulation, and scheduling backwash or element replacement cycles."
    }]
  },
  zh: {
    badge: "技术工程指南",
    title: "过滤原理与编织丝网力学解析",
    lead: "为工业过滤介质选型提供颗粒拦截机理、公称与绝对过滤精度标定、基础织型结构及核心计算术语的工程技术参考。",
    secATitle: "1. 表面过滤与深层过滤",
    secADesc: "拦截机理决定过滤介质的工作寿命、滤饼形成特性以及在线再生冲洗方案。",
    surfaceBadge: "二维几何截留",
    surfaceTitle: "表面过滤 (Surface Filtration)",
    surfaceSubtitle: "二维平面孔径几何拦截机制",
    surfaceDesc: "颗粒物严格被拦截在金属编织网的单层几何外表面。当颗粒粒径大于网孔尺寸时即被阻截，进而在网面形成渗透性滤饼，提供精准的截留界限并支持快速在线反冲洗。",
    surfaceInterfaceTitle: "过滤交界面",
    surfaceInterfaceTag: "编织金属丝网表面",
    surfaceCleanDischarge: "↓↓ 清洁滤液流出侧 ↓↓",
    surfacePoints: ["介质形态：精密单层方孔编织网、密纹编织网 (平纹密纹)。", "拦截机理：几何尺寸物理排阻与网孔截留。", "再生清洗：极易通过在线脉冲反冲洗、机械刮刷或超声波清洗脱附滤饼。", "典型应用：工业筛分、颗粒分级、固液澄清初滤、高固含量循环浆液过滤。"],
    depthBadge: "三维立体曲折孔道",
    depthTitle: "深层过滤 (Depth Filtration)",
    depthSubtitle: "三维立体曲折孔道截留机制",
    depthDesc: "流体流经多层金属烧结网或烧结金属多孔复合结构。污染物渗透至介质内部，通过曲折微孔路径的惯性碰撞、吸附与多级渐变孔隙实现深层捕获。",
    depthMatrixTitle: "深层过滤介质层",
    depthMatrixTag: "烧结多层复合丝网",
    depthCoarseLayer: "粗孔保护层 (100–200 μm)",
    depthFineCore: "精密烧结过滤核心层 (1–20 μm)",
    depthSupportLayer: "重型结构支撑骨架网 (刚性基座)",
    depthPoints: ["介质形态：多层扩散烧结丝网层叠结构、烧结金属多孔元件。", "拦截机理：迂回微孔截留、内部惯性碰撞与梯度截流。", "再生清洗：纳污容量显著提升；通常需化学溶剂浸洗、热解脱除或高压脉冲反吹。", "典型应用：聚合物熔体挤出、亚微米级高温气体/液体净化、液压油精密过滤。"],
    secBTitle: "2. 公称精度与绝对精度标定",
    secBDesc: "明确过滤效率门槛与实验室检测验证方法，杜绝工业选型中的参数混淆。",
    nominalTitle: "公称精度 (Nominal Rating)",
    nominalRating: "60% – 90% 颗粒捕集效率",
    nominalDesc: "一种基于试验统计的标称指标，表示在标准试验工况下，滤材能够拦截特定尺寸颗粒群的大多数百分比（通常为60%至90%）。",
    nominalNotes: ["截留效率：通常标定为 60% 至 90% 颗粒截留。", "渗透风险：可形变软颗粒或高长宽比纤维在系统压差波动冲击下可能穿透网孔。", "测试基准：标准粉尘单次通过重量法效率测试。", "适用领域：粗滤、预过滤、保护性安检滤网，无严苛绝对截止要求的流体系统。"],
    absoluteTitle: "绝对精度 (Absolute Rating)",
    absoluteRating: "≥ 99.9% 绝对截留阈值",
    absoluteDesc: "在标准工况测试中，按标准标定 99.9%+ 无法穿过滤网最大孔径的玻璃微珠或刚性测试微粒的标准球体直径。",
    absoluteNotes: ["截留效率：在规定微米级别下测试效率 ≥ 99.9%（过滤比 Beta 值 β ≥ 1000）。", "刚性孔隙：金属编织或烧结节点固定，高操作压差下孔径不产生位移形变。", "验证标准：泡点试验法 (ASTM E128 / ISO 4003) 及多次通过法 (Multi-pass test)。", "适用领域：生物制药洁净流体、航空航天燃油过滤、高聚物纺丝喷丝头保护、半导体化工。"],
    secCTitle: "3. 常见工业丝网编织类型",
    secCDesc: "方孔网与密纹网基础织造构造、机械性能与典型工况选型概览。",
    weaveArchitectureLabel: "编织结构特征",
    weaveApplicationsLabel: "典型工业应用",
    weaveSpecLinkText: "查阅技术规格详情",
    weaves: [{
      id: "plain-weave",
      name: "平纹编织 (Plain Weave)",
      structure: "每根经丝交替从每根纬丝的上方和下方穿过，经纬丝径通常一致，网孔呈正方形透光孔。",
      use: "工业通用筛分、固体颗粒分选、液体预滤及机械护罩，精度覆盖 25–38 微米以上范围。",
      link: "/PlainWeaveWireMeshPage"
    }, {
      id: "twilled-weave",
      name: "斜纹编织 (Twilled Weave)",
      structure: "每根纬丝交替跨越两根经丝上方与两根经丝下方，允许在相同目数下采用更粗线径提高承载力。",
      use: "适用于高目数但需较强机械耐磨强度的场合，如重介质振动筛、高压泥浆过滤与重型筛分。",
      link: "/TwilledWeaveWireMeshPage"
    }, {
      id: "plain-dutch",
      name: "平纹密纹编织 (Plain Dutch Weave)",
      structure: "经丝较粗且间隙较宽，纬丝较细且高密度密排敲紧，形成无直视透光的微型楔形三角微孔。",
      use: "高压液压回路、燃油过滤与离心脱水机常用滤布，兼具 15–20 微米截留精度与高抗剪切刚度。",
      link: "/PlainDutchWeavePage"
    }, {
      id: "twilled-dutch",
      name: "斜纹密纹编织 (Twilled Dutch Weave)",
      structure: "融合斜纹穿综与高密纬丝排列，形成双层曲折微孔流道，孔隙弯曲致密。",
      use: "传统编织金属丝网中精度最高织型（可达 1–2 微米公称/绝对截留），广泛用于航天及精细化工。",
      link: "/TwillDutchWeavePage"
    }, {
      id: "reverse-dutch",
      name: "反向密纹编织 (Reverse Dutch Weave)",
      structure: "经纬配置颠倒：经丝密集细致，纬丝较粗作为受力支撑，沿纵向具备极高的抗拉断裂强力。",
      use: "专为全自动塑料挤出机连续换网带 (Auto-Screen) 与高剪切工况设计，耐受极高纵向牵引力。",
      link: "/ReverseDutchWeavePage"
    }],
    secDTitle: "4. 工程术语表与核心计算指标",
    secDDesc: "金属丝网工程选型、流体力学核算与图纸规格标注的权威术语定义。",
    glossaryRelevanceLabel: "工程相关性：",
    glossary: [{
      term: "目数 (Mesh Count)",
      symbol: "N (Mesh)",
      definition: "每英寸 (25.4 mm) 长度内包含的网孔数量或丝数，从任一金属丝中心线开始计数。",
      significance: "决定编织密度的基础物理量。矩形织网表述为经向目数 × 纬向目数。"
    }, {
      term: "孔径 / 网孔净空 (Aperture)",
      symbol: "w",
      definition: "相邻两根平行金属丝之间的净空间隙宽度，工程上通常以微米 (μm) 或毫米 (mm) 计量。",
      significance: "决定表面过滤绝对物理截留界限的核心几何尺寸；公式：w = (25.4 / N) - d。"
    }, {
      term: "丝径 (Wire Diameter)",
      symbol: "d",
      definition: "编织金属丝在拉拔及编织定型后的实际标称直径规格。",
      significance: "决定滤网的耐压破裂强度、耐磨损耗周期以及整体网面挺度与柔韧性。"
    }, {
      term: "开孔率 (Open Area)",
      symbol: "Fo (%)",
      definition: "网孔净流通截面积占整个滤网总表面积的百分比比例。",
      significance: "直接决定流体流通阻力损失、透流线速度与单位有效通量容量。"
    }, {
      term: "微米精度 (Micron Rating)",
      symbol: "μm",
      definition: "标示过滤介质颗粒分离排除能力的标准长度单位（1 微米 = 0.001 毫米）。",
      significance: "区分工业粗筛 (>100 μm)、常规澄清微滤 (10–100 μm) 与精密微孔截留 (1–10 μm)。"
    }, {
      term: "过滤压差 (Differential Pressure)",
      symbol: "ΔP",
      definition: "流体流经滤网元件前后的上游入侧与下游净侧的流体静压差降值。",
      significance: "用于监控滤芯纳污饱和程度、评判阻力损耗及触发在线反冲洗或停机换件流程。"
    }]
  },
  es: {
    badge: "Guía técnica de ingeniería",
    title: "Principios de filtración y mecánica de mallas",
    lead: "Referencia técnica sobre mecanismos de retención de partículas, normas de clasificación, arquitecturas de tejido y terminología básica para la selección de mallas industriales.",
    secATitle: "1. Filtración superficial frente a filtración en profundidad",
    secADesc: "El mecanismo de retención determina la vida útil del medio, la formación de torta y el método de regeneración.",
    surfaceBadge: "Corte geométrico 2D",
    surfaceTitle: "Filtración superficial",
    surfaceSubtitle: "Retención bidimensional por formación de torta de partículas",
    surfaceDesc: "Las partículas quedan retenidas estrictamente en los límites geométricos de la superficie exterior de la malla tejida. Al superar el tamaño del poro, se forma una torta permeable que proporciona un corte de tamaño preciso y permite un retrolavado rápido.",
    surfaceInterfaceTitle: "INTERFAZ DE FILTRACIÓN",
    surfaceInterfaceTag: "SUPERFICIE DE MALLA TEJIDA",
    surfaceCleanDischarge: "↓↓ Descarga de filtrado limpio ↓↓",
    surfacePoints: ["Configuración del medio: Malla metálica de una sola capa de tejido liso o tejido Dutch de precisión.", "Mecanismo: Intercepción geométrica y exclusión por tamaño de abertura.", "Regeneración: Limpieza sencilla mediante retrolavado por pulsos inversos o limpieza ultrasónica.", "Aplicación principal: Tamizado, clasificación de partículas, separación sólido-líquido y lodos continuos con alto contenido de sólidos."],
    depthBadge: "Matriz tortuosa 3D",
    depthTitle: "Filtración en profundidad",
    depthSubtitle: "Retención tridimensional por trayectoria tortuosa",
    depthDesc: "El fluido avanza a través de una matriz multicapa o un compuesto sinterizado. Los contaminantes penetran en el núcleo y quedan atrapados en microcanales ramificados y tortuosos mediante impacto e interceptación.",
    depthMatrixTitle: "MATRIZ DE PROFUNDIDAD",
    depthMatrixTag: "COMPUESTO MULTICAPA SINTERIZADO",
    depthCoarseLayer: "Capa protectora gruesa (100–200 μm)",
    depthFineCore: "Núcleo filtrante sinterizado fino (1–20 μm)",
    depthSupportLayer: "Malla de soporte estructural rígida",
    depthPoints: ["Configuración del medio: Malla sinterizada unida por difusión multicapa o laminados metálicos sinterizados.", "Mecanismo: Captura por trayectoria tortuosa, impacto dinámico y retención de gradiente multinivel.", "Regeneración: Mayor capacidad de retención de suciedad; limpieza química, desvolatilización térmica o retrolavado a alta presión.", "Aplicación principal: Extrusión de polímeros fundidos, filtración crítica de gas/líquido submicrónica, circuitos hidráulicos."],
    secBTitle: "2. Clasificación micrométrica nominal frente a absoluta",
    secBDesc: "Clarificación de umbrales de eficiencia y procedimientos de ensayo en laboratorio para evitar selecciones incorrectas.",
    nominalTitle: "Clasificación micrométrica nominal",
    nominalRating: "Eficiencia del 60% – 90%",
    nominalDesc: "Evaluación comercial que indica la capacidad de la malla para retener un porcentaje predominante de partículas de una dimensión determinada en condiciones de prueba normalizadas.",
    nominalNotes: ["Eficiencia de retención: Habitualmente entre el 60% y el 90% de captura de partículas.", "Retención variable: Partículas deformables o fibras alargadas pueden pasar ante picos de presión diferencial.", "Base de ensayo: Métodos gravimétricos de paso simple con polvo de prueba normalizado.", "Uso previsto: Prefiltración, desbaste y tamizado de seguridad donde no se exige corte absoluto."],
    absoluteTitle: "Clasificación micrométrica absoluta",
    absoluteRating: "Umbral de retención ≥ 99,9%",
    absoluteDesc: "Diámetro esférico exacto de microesferas de vidrio que no atraviesan la mayor abertura registrada de la tela filtrante en condiciones de presión normalizadas.",
    absoluteNotes: ["Eficiencia de retención: 99,9% o superior (relación Beta β ≥ 1000) en el grado verificado.", "Geometría de poro rígida: La estabilidad estructural evita la deformación de aperturas bajo presión de línea.", "Métodos de validación: Prueba de punto de burbuja (ASTM E128 / ISO 4003) y ensayo multipaso.", "Uso previsto: Procesos farmacéuticos críticos, combustible de aviación, hilado de polímeros y fluidos semiconductores."],
    secCTitle: "3. Tipos comunes de tejido industrial",
    secCDesc: "Descripción general de estructuras de tejido liso y Dutch, dinámica estructural y adecuación al proceso.",
    weaveArchitectureLabel: "Arquitectura del tejido",
    weaveApplicationsLabel: "Aplicaciones industriales típicas",
    weaveSpecLinkText: "Ver especificaciones técnicas",
    weaves: [{
      id: "plain-weave",
      name: "Malla de alambre de tejido liso",
      structure: "Cada alambre de urdimbre cruza alternativamente por encima y por debajo de cada alambre de trama. Los diámetros son generalmente idénticos, formando aberturas cuadradas regulares.",
      use: "Utilizado ampliamente en clasificación de partículas, tamizado, filtración de líquidos y protección arquitectónica hasta 25–38 micras.",
      link: "/PlainWeaveWireMeshPage"
    }, {
      id: "twilled-weave",
      name: "Malla de alambre de tejido sarga",
      structure: "Cada alambre de trama pasa alternativamente sobre dos y bajo dos alambres de urdimbre, permitiendo alambres más gruesos respecto a la abertura.",
      use: "Adecuado cuando se requieren alambres resistentes en mallas densas; idóneo para lodos abrasivos, cargas mecánicas altas y cribas vibratorias exigentes.",
      link: "/TwilledWeaveWireMeshPage"
    }, {
      id: "plain-dutch",
      name: "Tejido Dutch liso (PDW)",
      structure: "Alambres de urdimbre más gruesos y espaciados combinados con alambres de trama delgados y tupidos, generando aberturas triangulares cerradas a la luz.",
      use: "Estándar para filtración de líquidos a alta presión y combustibles hidráulicos, con retención compacta (hasta 15–20 micras) y alta resistencia al cizallamiento.",
      link: "/PlainDutchWeavePage"
    }, {
      id: "twilled-dutch",
      name: "Tejido Dutch cruzado (TDW)",
      structure: "Combina el patrón de sarga con una trama Dutch compacta, creando una doble capa de alambres con trayectorias de flujo tortuosas y microscópicas.",
      use: "Ofrece la retención más fina entre las telas tejidas estándar (hasta 1–2 micras nominales/absolutas); ideal para aeroespacial, dosificación química y polímeros finos.",
      link: "/TwillDutchWeavePage"
    }, {
      id: "reverse-dutch",
      name: "Tejido Dutch inverso (RDW)",
      structure: "Configuración invertida con alambres de urdimbre gruesos muy juntos y alambres de trama más delgados, proporcionando altísima resistencia a la tracción longitudinal.",
      use: "Diseñado específicamente para cambiadores de filtros continuos de polímeros fundidos (Auto-Screen) y bandas de extrusión sometidas a intensa tracción.",
      link: "/ReverseDutchWeavePage"
    }],
    secDTitle: "4. Glosario técnico y parámetros de ingeniería",
    secDDesc: "Definiciones esenciales y términos hidráulicos utilizados en la especificación y dimensionamiento de mallas de alambre.",
    glossaryRelevanceLabel: "Relevancia en ingeniería:",
    glossary: [{
      term: "Número de mallas (Mesh Count)",
      symbol: "N (Mesh)",
      definition: "Número de aberturas o alambres por pulgada lineal (25,4 mm), contados desde el centro de cualquier alambre.",
      significance: "Determina la densidad básica del tejido. En tejidos rectangulares se expresa como Urdimbre × Trama."
    }, {
      term: "Abertura (Pore Opening)",
      symbol: "w",
      definition: "Distancia libre entre dos alambres paralelos contiguos de urdimbre o trama, expresada en micras (μm) o milímetros (mm).",
      significance: "Parámetro geométrico decisivo para el corte superficial; calculado como w = (25,4 / N) - d."
    }, {
      term: "Diámetro del alambre (Wire Diameter)",
      symbol: "d",
      definition: "Calibre o grosor nominal de los alambres de urdimbre y trama antes o después del tejido.",
      significance: "Determina la resistencia al estallido, la resistencia al desgaste por abrasión y la flexibilidad de la tela."
    }, {
      term: "Área abierta (Open Area)",
      symbol: "Fo (%)",
      definition: "Proporción porcentual de la superficie total de la malla ocupada por orificios libres frente al área de alambre sólido.",
      significance: "Influye directamente en la velocidad de paso del fluido, la pérdida de carga y la capacidad de caudal volumétrico."
    }, {
      term: "Clasificación en micras (Micron Rating)",
      symbol: "μm",
      definition: "Unidad lineal normalizada (1 micra = 0,001 mm) que indica la capacidad de exclusión de partículas del medio filtrante.",
      significance: "Establece la categoría de retención, distinguiendo entre tamizado grueso (>100 μm) y microfiltración (1–100 μm)."
    }, {
      term: "Presión diferencial (Differential Pressure)",
      symbol: "ΔP",
      definition: "Pérdida de presión hidráulica entre la entrada aguas arriba y la salida limpia aguas abajo del elemento filtrante.",
      significance: "Métrica fundamental para supervisar la colmatación, la acumulación de torta y programar ciclos de retrolavado."
    }]
  },
  de: {
    badge: "Technischer Leitfaden",
    title: "Filtrationsprinzipien & Gewebemechanik",
    lead: "Technisches Nachschlagewerk zu Partikelabscheidemechanismen, Bewertungsnormen, Webarten und zentralen Berechnungsgrundlagen für die industrielle Gewebeauswahl.",
    secATitle: "1. Oberflächenfiltration im Vergleich zur Tiefenfiltration",
    secADesc: "Der Rückhaltemechanismus bestimmt Standzeit, Filterkuchenbildung und Regenerationsmethoden.",
    surfaceBadge: "2D-Geometrischer Trennschnitt",
    surfaceTitle: "Oberflächenfiltration",
    surfaceSubtitle: "Zweidimensionale Partikelkuchen-Rückhaltung",
    surfaceDesc: "Partikel werden direkt an den geometrischen Öffnungen der äußeren Gewebeoberfläche abgeschieden. Sobald Partikel die Maschenweite überschreiten, entsteht ein durchlässiger Filterkuchen mit scharfer Trenngrenze, der sich schnell rückspülen lässt.",
    surfaceInterfaceTitle: "FILTERGRENZFLÄCHE",
    surfaceInterfaceTag: "DRAHTGEWEBEOBERFLÄCHE",
    surfaceCleanDischarge: "↓↓ Austritt des gereinigten Filtrats ↓↓",
    surfacePoints: ["Medienkonfiguration: Präzises einlagiges Quadrat- oder Tressengewebe.", "Mechanismus: Geometrische Interzeption und Maschenweiten-Ausschluss.", "Regeneration: Einfach zu reinigen durch gepulste Rückspülung oder Ultraschall.", "Hauptanwendung: Siebung, Partikelklassierung, Fest-Flüssig-Trennung, Suspensionen mit hohem Feststoffanteil."],
    depthBadge: "3D-Verzweigte Matrix",
    depthTitle: "Tiefenfiltration",
    depthSubtitle: "Dreidimensionale Partikelerfassung in verzweigten Poren",
    depthDesc: "Das Fluid strömt durch eine mehrlagige Matrix oder einen gesinterten Verbund. Verunreinigungen dringen in das Innere ein und werden in gewundenen Mikrokanälen durch Prallwirkung und Adsorption zurückgehalten.",
    depthMatrixTitle: "TIEFENMATRIX",
    depthMatrixTag: "GESINTERTER MEHRLAGENVERBUND",
    depthCoarseLayer: "Grobe Schutzschicht (100–200 μm)",
    depthFineCore: "Feiner Sinterfilterkern (1–20 μm)",
    depthSupportLayer: "Formstabiles Trägergewebe (Stützlage)",
    depthPoints: ["Medienkonfiguration: Mehrlagiges diffusionsverschweißtes Sintergewebe oder Metallfaservlies.", "Mechanismus: Porenkanal-Rückhaltung, dynamischer Prall und mehrstufige Gradientenabscheidung.", "Regeneration: Sehr hohe Schmutzaufnahmekapazität; Reinigung über Lösemittel, Pyrolyse oder Hochdruck-Rückspülung.", "Hauptanwendung: Polymerschmelzefiltration, hochreine Gas-/Flüssigkeitsfiltration, Hydrauliksysteme."],
    secBTitle: "2. Nominaler vs. absoluter Mikron-Abscheidegrad",
    secBDesc: "Differenzierung von Effizienzschwellen und Laborprüfmethoden zur Vermeidung von Auslegungsfehlern.",
    nominalTitle: "Nominaler Abscheidegrad",
    nominalRating: "60 % – 90 % Wirkungsgrad",
    nominalDesc: "Ein praxisbezogener Nennwert, der angibt, dass das Filtermedium den überwiegenden Anteil von Partikeln einer festgelegten Größe unter Standardtestbedingungen abscheidet.",
    nominalNotes: ["Rückhalteeffizienz: Üblicherweise zwischen 60 % und 90 % Partikelabscheidung spezifiziert.", "Variables Verhalten: Verformbare Partikel oder Fasern können bei Druckspitzen durch die Poren wandern.", "Prüfbasis: Gravimetrische Single-Pass-Verfahren mit Standard-Prüfstaub.", "Einsatzbereich: Vorfiltration, Grobsiebung und Schutzsiebe ohne zwingenden Absolutschnitt."],
    absoluteTitle: "Absoluter Abscheidegrad",
    absoluteRating: "≥ 99,9 % Rückhalteschwelle",
    absoluteDesc: "Exakter Durchmesser kugelförmiger Glaskugeln, die die größte nachgewiesene Öffnung des Filtermediums unter definierten Testdrücken nicht mehr passieren können.",
    absoluteNotes: ["Rückhalteeffizienz: 99,9 % oder höher (Beta-Verhältnis β ≥ 1000) bei angegebener Maschenweite.", "Starre Porengeometrie: Formstabilität verhindert das Weiten der Maschenöffnungen unter Betriebsdruck.", "Validierungsmethoden: Blaspunkttest (Bubble Point Test nach ASTM E128 / ISO 4003) und Multi-Pass-Prüfungen.", "Einsatzbereich: Pharmazeutische Prozesse, Flugkraftstoff, Spinnbrausen für Kunstfasern, Halbleiterchemikalien."],
    secCTitle: "3. Gängige industrielle Webarten",
    secCDesc: "Übersicht über Standard- und Tressengewebe, Festigkeitseigenschaften und verfahrenstechnische Eignung.",
    weaveArchitectureLabel: "Gewebegeometrie",
    weaveApplicationsLabel: "Typische Industrieanwendungen",
    weaveSpecLinkText: "Technische Spezifikationen anzeigen",
    weaves: [{
      id: "plain-weave",
      name: "Drahtgewebe in Leinwandbindung",
      structure: "Jeder Kett- und Schussdraht verläuft abwechselnd über und unter dem nächsten Draht. Üblicherweise gleiche Drahtstärken bei quadratischer Maschenform.",
      use: "Weit verbreitet zur Partikelklassierung, Siebung, Flüssigkeitsfiltration und Schutzgittern bis 25–38 Mikron.",
      link: "/PlainWeaveWireMeshPage"
    }, {
      id: "twilled-weave",
      name: "Drahtgewebe in Köperbindung",
      structure: "Jeder Schussdraht verläuft abwechselnd über zwei und unter zwei Kettdrähten, was stärkere Drähte bei gleicher Maschenzahl ermöglicht.",
      use: "Gewählt für hohe mechanische Festigkeit bei feineren Teilungen; geeignet für abrasive Schlämme, Vibrationssiebe und hohe mechanische Belastungen.",
      link: "/TwilledWeaveWireMeshPage"
    }, {
      id: "plain-dutch",
      name: "Glatte Tresse (PDW)",
      structure: "Kräftige Kettdrähte in weiterem Abstand kombiniert mit dünnen, dicht aneinander geschlagenen Schussdrähten bilden keilförmige Dreiecksporen.",
      use: "Standard für Hochdruckhydraulik und Kraftstofffiltration; bietet präzisen Rückhalt (bis 15–20 Mikron) bei hoher Scherstabilität.",
      link: "/PlainDutchWeavePage"
    }, {
      id: "twilled-dutch",
      name: "Köpertresse (TDW)",
      structure: "Verbindet Köperbindung mit dicht geschlagenen Schussdrähten; zweilagige Schussanordnung erzeugt gewundene mikroskopische Strömungskanäle.",
      use: "Feinster Rückhalt unter Standardgeweben (bis 1–2 Mikron nominal/absolut); ideal für Luft- und Raumfahrt, Chemiedosierung und Feinpolymere.",
      link: "/TwillDutchWeavePage"
    }, {
      id: "reverse-dutch",
      name: "Panzertresse / Umgekehrte Tresse (RDW)",
      structure: "Umgekehrte Struktur mit dicht liegenden starken Kettdrähten und feinen Schussdrähten; extrem hohe Zugfestigkeit in Längsrichtung.",
      use: "Speziell entwickelt für kontinuierliche automatische Siebbandwechsler (Auto-Screen) bei der Kunststoffextrusion unter hoher Zugspannung.",
      link: "/ReverseDutchWeavePage"
    }],
    secDTitle: "4. Technisches Glossar & Auslegungsparameter",
    secDDesc: "Zentrale Definitionen und strömungstechnische Begriffe für die Auslegung von Drahtgeweben.",
    glossaryRelevanceLabel: "Technische Relevanz:",
    glossary: [{
      term: "Maschenzahl (Mesh Count)",
      symbol: "N (Mesh)",
      definition: "Anzahl der Maschen oder Drähte pro linearem Zoll (25,4 mm), gemessen von Drahtmitte zu Drahtmitte.",
      significance: "Bestimmt die grundlegende Webdichte. Bei Rechteckmaschen angegeben als Kette × Schuss."
    }, {
      term: "Maschenweite (Aperture)",
      symbol: "w",
      definition: "Lichte Weite zwischen zwei benachbarten parallelen Drähten in Mikrometer (μm) oder Millimeter (mm).",
      significance: "Entscheidendes geometrisches Maß für den Oberflächenrückhalt; Berechnung: w = (25,4 / N) - d."
    }, {
      term: "Drahtdurchmesser (Wire Diameter)",
      symbol: "d",
      definition: "Nenndurchmesser der Kett- und Schussdrähte vor bzw. nach dem Webprozess.",
      significance: "Bestimmt den Berstdruck, die Verschleißfestigkeit gegen Abrasion und die Gewebesteifigkeit."
    }, {
      term: "Offene Siebfläche (Open Area)",
      symbol: "Fo (%)",
      definition: "Prozentualer Anteil der freien Durchtrittsöffnungen an der Gesamtfläche des Gewebes.",
      significance: "Bestimmt Strömungsgeschwindigkeit, Druckverlust über dem Siebelement und Durchsatzleistung."
    }, {
      term: "Mikron-Abscheidegrad (Micron Rating)",
      symbol: "μm",
      definition: "Genormte Längeneinheit (1 Mikron = 0,001 mm) zur Klassifizierung des Partikelrückhaltevermögens.",
      significance: "Unterteilt in Grobtrennung (>100 μm), Klärfiltration (10–100 μm) und Mikrofiltration (1–10 μm)."
    }, {
      term: "Druckverlust (Differential Pressure)",
      symbol: "ΔP",
      definition: "Hydraulischer Druckunterschied zwischen Anström- und Filtratseite des Siebelements.",
      significance: "Wichtige Kennzahl zur Überwachung der Filterbeladung und Einleitung von Rückspül- oder Wechselzyklen."
    }]
  },
  fr: {
    badge: "Guide technique d'ingénierie",
    title: "Principes de filtration & mécanique des toiles",
    lead: "Référence technique couvrant les mécanismes de capture, les normes de filtration, les armures de tissage et la terminologie de calcul pour le dimensionnement industriel.",
    secATitle: "1. Filtration de surface vs filtration en profondeur",
    secADesc: "Le mécanisme de rétention détermine la durée de vie du média, le colmatage en gâteau et les méthodes de régénération.",
    surfaceBadge: "Coupure géométrique 2D",
    surfaceTitle: "Filtration de surface",
    surfaceSubtitle: "Rétention bidimensionnelle par formation de gâteau",
    surfaceDesc: "Les particules sont arrêtées directement à la frontière géométrique de la surface tissée. Dès qu'elles dépassent l'ouverture de maille, un gâteau perméable se forme, offrant une coupure nette et un décolmatage rapide à contre-courant.",
    surfaceInterfaceTitle: "INTERFACE DE FILTRATION",
    surfaceInterfaceTag: "SURFACE DE TOILE TISSÉE",
    surfaceCleanDischarge: "↓↓ Évacuation du filtrat propre ↓↓",
    surfacePoints: ["Configuration du média : Toile métallique monocouche à tissage uni ou tissage hollandais.", "Mécanisme : Interception géométrique et exclusion dimensionnelle par l'ouverture.", "Régénération : Nettoyage aisé par rétrolavage pulsé ou cuve à ultrasons.", "Application principale : Tamisage, classification granulométrique, séparation solide-liquide, boues denses."],
    depthBadge: "Matrice tortueuse 3D",
    depthTitle: "Filtration en profondeur",
    depthSubtitle: "Rétention tridimensionnelle le long de microcanaux tortueux",
    depthDesc: "Le fluide traverse une matrice multicouche ou un composite fritté. Les impuretés pénètrent dans l'épaisseur et sont piégées dans des microcanaux enchevêtrés par impact inertiel et rétention par gradient.",
    depthMatrixTitle: "MATRICE DE PROFONDEUR",
    depthMatrixTag: "COMPOSITE FRITTÉ MULTICOUCHE",
    depthCoarseLayer: "Couche de protection grossière (100–200 μm)",
    depthFineCore: "Cœur filtrant fritté fin (1–20 μm)",
    depthSupportLayer: "Toile de support structurel robuste",
    depthPoints: ["Configuration du média : Toile frittée multicouche liée par diffusion thermique.", "Mécanisme : Piégeage tortueux, impact dynamique et rétention progressive.", "Régénération : Capacité de rétention de poussière accrue ; nettoyage chimique, pyrolyse ou rétrolavage haute pression.", "Application principale : Extrusion de polymères fondus, filtration de fluides critiques submicroniques, circuits hydrauliques."],
    secBTitle: "2. Seuil de filtration nominal vs absolu",
    secBDesc: "Clarification des seuils d'efficacité et des protocoles de mesure en laboratoire pour éviter tout sous-dimensionnement.",
    nominalTitle: "Seuil de filtration nominal",
    nominalRating: "Efficacité de 60 % à 90 %",
    nominalDesc: "Indication commerciale empirique exprimant la capacité du média à retenir une proportion majoritaire de particules d'une taille donnée dans des conditions d'essai normalisées.",
    nominalNotes: ["Efficacité de rétention : Généralement comprise entre 60 % et 90 % de capture particulaire.", "Rétention variable : Les particules déformables ou fibres peuvent migrer sous des à-coups de pression.", "Méthode d'essai : Mesure gravimétrique en un seul passage avec poussière d'essai standard.", "Usage prévu : Préfiltration, dégrillage et filtration de sécurité sans impératif de coupure stricte."],
    absoluteTitle: "Seuil de filtration absolu",
    absoluteRating: "Seuil de rétention ≥ 99,9 %",
    absoluteDesc: "Diamètre exact des billes de verre sphériques ne franchissant pas le plus grand pore mesuré de la toile sous une pression d'épreuve standardisée.",
    absoluteNotes: ["Efficacité de rétention : 99,9 % ou plus (taux Beta β ≥ 1000) au seuil mesuré.", "Géométrie de pore rigide : Les nœuds tissés ou frittés empêchent toute dilatation sous pression de ligne.", "Méthodes de validation : Test au point de bulle (ASTM E128 / ISO 4003) et essais multi-passes.", "Usage prévu : Procédés pharmaceutiques critiques, carburant aéronautique, filières polymères, chimie fine."],
    secCTitle: "3. Armures de tissage industriel courantes",
    secCDesc: "Présentation des tissages unis et hollandais, de leur résistance mécanique et de leurs domaines d'application.",
    weaveArchitectureLabel: "Architecture du tissage",
    weaveApplicationsLabel: "Applications industrielles types",
    weaveSpecLinkText: "Voir les spécifications techniques",
    weaves: [{
      id: "plain-weave",
      name: "Toile métallique en tissage uni",
      structure: "Chaque fil de chaîne passe alternativement au-dessus et en-dessous de chaque fil de trame. Fils de diamètres égaux formant des mailles carrées régulières.",
      use: "Tamisage industriel, séparation solide, préfiltration de liquides et grilles de protection jusqu'à 25–38 microns.",
      link: "/PlainWeaveWireMeshPage"
    }, {
      id: "twilled-weave",
      name: "Toile métallique en tissage sergé",
      structure: "Chaque fil de trame passe alternativement sur deux puis sous deux fils de chaîne, autorisant des diamètres de fil plus élevés à maille égale.",
      use: "Indiqué pour des charges mécaniques importantes et toiles fines ; convient aux boues abrasives et cribles vibrants.",
      link: "/TwilledWeaveWireMeshPage"
    }, {
      id: "plain-dutch",
      name: "Tissage hollandais uni (PDW)",
      structure: "Fils de chaîne plus espacés et robustes combinés à des fils de trame fins et serrés, formant des ouvertures cunéiformes sans passage direct de lumière.",
      use: "Standard pour la filtration de fluides sous haute pression et circuits hydrauliques (jusqu'à 15–20 microns) avec haute tenue au cisaillement.",
      link: "/PlainDutchWeavePage"
    }, {
      id: "twilled-dutch",
      name: "Tissage hollandais sergé (TDW)",
      structure: "Associe une armure sergée à une trame hollandaise très serrée ; forme un réseau microscopique à double couche et écoulement tortueux.",
      use: "Plus fine coupure parmi les toiles tissées standard (jusqu'à 1–2 microns nominal/absolu) ; idéal pour l'aéronautique et la chimie de spécialité.",
      link: "/TwillDutchWeavePage"
    }, {
      id: "reverse-dutch",
      name: "Tissage hollandais inversé (RDW)",
      structure: "Configuration inversée : chaîne dense et résistante, trame plus fine servant de liant ; résistance extrême à la traction longitudinale.",
      use: "Développé pour les changeurs de filtres continus d'extrusion de plastique (Auto-Screen) soumis à une forte traction axiale.",
      link: "/ReverseDutchWeavePage"
    }],
    secDTitle: "4. Glossaire technique & métriques de calcul",
    secDDesc: "Définitions et grandeurs hydrauliques clés pour le dimensionnement et la prescription des toiles métalliques.",
    glossaryRelevanceLabel: "Pertinence technique :",
    glossary: [{
      term: "Nombre de mailles (Mesh Count)",
      symbol: "N (Mesh)",
      definition: "Nombre d'ouvertures ou de fils par pouce linéaire (25,4 mm), compté depuis l'axe central d'un fil.",
      significance: "Définit la densité de tissage. S'exprime en Chaîne × Trame pour les mailles rectangulaires."
    }, {
      term: "Ouverture de maille (Aperture)",
      symbol: "w",
      definition: "Distance libre entre deux fils parallèles adjacents, exprimée en microns (μm) ou en millimètres (mm).",
      significance: "Grandeur géométrique déterminant la coupure en surface ; formule : w = (25,4 / N) - d."
    }, {
      term: "Diamètre du fil (Wire Diameter)",
      symbol: "d",
      definition: "Diamètre nominal des fils de chaîne et de trame avant ou après le processus de tissage.",
      significance: "Conditionne la pression d'éclatement, la résistance à l'abrasion et la rigidité de la toile."
    }, {
      term: "Surface ouverte (Open Area)",
      symbol: "Fo (%)",
      definition: "Proportion en pourcentage de la surface totale occupée par les vides d'ouverture par rapport au métal plein.",
      significance: "Détermine directement la vitesse de passage du fluide, la perte de charge et le débit admissible."
    }, {
      term: "Seuil en microns (Micron Rating)",
      symbol: "μm",
      definition: "Unité linéaire (1 micron = 0,001 mm) indiquant le calibre de particules retenues par le média.",
      significance: "Établit la classe de filtration entre tamisage grossier (>100 μm) et microfiltration fine (1–100 μm)."
    }, {
      term: "Perte de charge (Differential Pressure)",
      symbol: "ΔP",
      definition: "Différence de pression statique entre l'amont brut et l'aval propre de l'élément filtrant.",
      significance: "Paramètre crucial pour mesurer le colmatage, planifier les cycles de lavage ou le remplacement."
    }]
  },
  pt: {
    badge: "Guia técnico de engenharia",
    title: "Princípios de filtração e mecânica de telas",
    lead: "Referência técnica cobrindo mecanismos de retenção de partículas, normas de classificação, estruturas de tecelagem e terminologia essencial para o dimensionamento industrial.",
    secATitle: "1. Filtração superficial vs filtração em profundidade",
    secADesc: "O mecanismo de retenção determina a vida útil do meio, a formação da torta e os métodos de regeneração.",
    surfaceBadge: "Corte geométrico 2D",
    surfaceTitle: "Filtração superficial",
    surfaceSubtitle: "Retenção bidimensional por formação de torta de partículas",
    surfaceDesc: "As partículas são retidas rigorosamente nos limites geométricos da superfície externa da tela tecida. Ao ultrapassarem a abertura dos poros, forma-se uma torta permeável que garante corte preciso e retrolavagem rápida.",
    surfaceInterfaceTitle: "INTERFACE DE FILTRAÇÃO",
    surfaceInterfaceTag: "SUPERFÍCIE DE TELA TECIDA",
    surfaceCleanDischarge: "↓↓ Descarga de filtrado limpo ↓↓",
    surfacePoints: ["Configuração do meio: Tela metálica monocamada de trama lisa ou trama holandesa de precisão.", "Mecanismo: Interceptação geométrica e exclusão física pela abertura da malha.", "Regeneração: Limpeza facilitada por retrolavagem pulsada reversa ou banho ultrassônico.", "Aplicação principal: Peneiramento, classificação granulométrica, separação sólido-líquido e pastas de alto teor de sólidos."],
    depthBadge: "Matriz tortuosa 3D",
    depthTitle: "Filtração em profundidade",
    depthSubtitle: "Retenção tridimensional por canais microscópicos tortuosos",
    depthDesc: "O fluido percorre uma matriz multicamadas ou um composto sinterizado. Os contaminantes penetram na espessura e ficam retidos em microcanais ramificados por impacto inercial e aprisionamento por gradiente.",
    depthMatrixTitle: "MATRIZ DE PROFUNDIDADE",
    depthMatrixTag: "COMPOSTO SINTERIZADO MULTICAMADAS",
    depthCoarseLayer: "Camada protetora grossa (100–200 μm)",
    depthFineCore: "Núcleo filtrante sinterizado fino (1–20 μm)",
    depthSupportLayer: "Malha de suporte estrutural reforçada",
    depthPoints: ["Configuração do meio: Tela sinterizada multicamadas unida por difusão térmica.", "Mecanismo: Captura por canais tortuosos, impacto dinâmico e retenção por gradiente progressivo.", "Regeneração: Alta capacidade de retenção de sujeira; limpeza química, pirólise ou retrolavagem de alta pressão.", "Aplicação principal: Extrusão de polímeros fundidos, filtração crítica submicrométrica de gases/líquidos, circuitos hidráulicos."],
    secBTitle: "2. Grau de retenção micrométrico nominal vs absoluto",
    secBDesc: "Esclarecimento de limites de eficiência e procedimentos de validação laboratorial para evitar subdimensionamento.",
    nominalTitle: "Grau de retenção nominal",
    nominalRating: "Eficiência de 60% a 90%",
    nominalDesc: "Classificação comercial indicando a capacidade do meio em reter uma fração predominante de partículas de tamanho especificado sob condições normatizadas de teste.",
    nominalNotes: ["Eficiência de retenção: Tipicamente especificada entre 60% e 90% de captura particulada.", "Retenção variável: Partículas deformáveis ou fibras podem atravessar sob oscilações de pressão diferencial.", "Método de ensaio: Ensaios gravimétricos de passagem única com poeira padrão de teste.", "Uso recomendado: Pré-filtração, desbaste e telas de segurança onde o corte absoluto não é indispensável."],
    absoluteTitle: "Grau de retenção absoluto",
    absoluteRating: "Limite de retenção ≥ 99,9%",
    absoluteDesc: "Diâmetro esférico exato de microesferas de vidro que não atravessam a maior abertura registrada da tela sob condições padronizadas de pressão.",
    absoluteNotes: ["Eficiência de retenção: 99,9% ou superior (razão Beta β ≥ 1000) no nível micrométrico medido.", "Geometria de poro rígida: Nós metálicos firmes impedem a deformação das aberturas sob pressão operacional.", "Métodos de validação: Teste do ponto de bolha (ASTM E128 / ISO 4003) e ensaios multipass.", "Uso recomendado: Processos farmacêuticos críticos, combustível de aviação, fiação de polímeros e fluidos semicondutores."],
    secCTitle: "3. Tipos comuns de tecelagem industrial",
    secCDesc: "Visão geral de estruturas de tecelagem lisa e holandesa, resistência mecânica e adequação aos processos.",
    weaveArchitectureLabel: "Arquitetura da tecelagem",
    weaveApplicationsLabel: "Aplicações industriais típicas",
    weaveSpecLinkText: "Ver especificações técnicas",
    weaves: [{
      id: "plain-weave",
      name: "Tela de arame com trama lisa",
      structure: "Cada fio de urdume cruza alternadamente por cima e por baixo de cada fio de trama. Fios com diâmetros idênticos formando aberturas quadradas regulares.",
      use: "Classificação de partículas, peneiramento, filtração de líquidos e telas de proteção até 25–38 mícrons.",
      link: "/PlainWeaveWireMeshPage"
    }, {
      id: "twilled-weave",
      name: "Tela de arame com trama sarja",
      structure: "Cada fio de trama passa alternadamente sobre dois e sob dois fios de urdume, permitindo fios mais grossos para uma mesma abertura.",
      use: "Indicado quando se exigem fios mais pesados em malhas finas; suporta pastas abrasivas, cargas elevadas e peneiras vibratórias.",
      link: "/TwilledWeaveWireMeshPage"
    }, {
      id: "plain-dutch",
      name: "Trama holandesa lisa (PDW)",
      structure: "Fios de urdume mais grossos e espaçados combinados a fios de trama finos e compactados, formando aberturas triangulares fechadas à luz.",
      use: "Padrão para filtração de líquidos sob alta pressão e combustíveis hidráulicos (até 15–20 mícrons) com alta rigidez contra cisalhamento.",
      link: "/PlainDutchWeavePage"
    }, {
      id: "twilled-dutch",
      name: "Trama holandesa sarja (TDW)",
      structure: "Combina a sarja com fios de trama densamente compactados, criando dupla camada com microcanais de fluxo tortuoso.",
      use: "Oferece a retenção mais fina entre as telas tecidas padrão (até 1–2 mícrons nominal/absoluto); ideal para aeroespacial e química fina.",
      link: "/TwillDutchWeavePage"
    }, {
      id: "reverse-dutch",
      name: "Trama holandesa invertida (RDW)",
      structure: "Estrutura invertida: urdume denso e resistente e trama mais flexível, proporcionando resistência altíssima à tração longitudinal.",
      use: "Projetado especificamente para trocadores contínuos de telas em extrusão de polímeros (Auto-Screen) sujeitos a tração intensa.",
      link: "/ReverseDutchWeavePage"
    }],
    secDTitle: "4. Glossário técnico e parâmetros de cálculo",
    secDDesc: "Definições essenciais e grandezas hidráulicas aplicadas na especificação de telas metálicas.",
    glossaryRelevanceLabel: "Relevância em engenharia:",
    glossary: [{
      term: "Contagem de malha (Mesh Count)",
      symbol: "N (Mesh)",
      definition: "Número de aberturas ou fios por polegada linear (25,4 mm), medido a partir do centro de qualquer fio.",
      significance: "Determina a densidade de tecelagem. Em malhas retangulares, é indicada como Urdume × Trama."
    }, {
      term: "Abertura da malha (Aperture)",
      symbol: "w",
      definition: "Distância livre entre dois fios paralelos adjacents de urdume ou trama, expressa em mícrons (μm) ou milímetros (mm).",
      significance: "Parâmetro geométrico decisivo para o corte superficial; calculado por: w = (25,4 / N) - d."
    }, {
      term: "Diâmetro do arame (Wire Diameter)",
      symbol: "d",
      definition: "Diâmetro nominal dos fios de urdume e trama antes ou após o processo de tecelagem.",
      significance: "Determina a resistência ao estouro, a vida útil contra abrasão e a rigidez da tela."
    }, {
      term: "Área aberta (Open Area)",
      symbol: "Fo (%)",
      definition: "Porcentagem da superfície total da tela ocupada pelas aberturas livres em relação à área sólida de arame.",
      significance: "Determina diretamente a velocidade de passagem do fluido, a perda de carga e a capacidade volumétrica."
    }, {
      term: "Classificação em mícrons (Micron Rating)",
      symbol: "μm",
      definition: "Unidade de medida padronizada (1 mícron = 0,001 mm) que indica a capacidade de exclusão de partículas do meio filtrante.",
      significance: "Define a classe de retenção entre separação grossa (>100 μm) e microfiltração fina (1–100 μm)."
    }, {
      term: "Queda de pressão (Differential Pressure)",
      symbol: "ΔP",
      definition: "Diferença de pressão hidráulica estática entre a entrada a montante e a saída a jusante do elemento filtrante.",
      significance: "Métrica fundamental para monitorar a colmatação, acúmulo de torta e programar retrolavagem."
    }]
  }
};
export default function FiltrationPrinciplesTechnicalGuide() {
  const {
    lang
  } = useLanguage();
  const t = pick(lang, copy);
  return <section id="filtrationprinciplestechnicalguide" data-nav="light" className="relative bg-background text-foreground mf-section border-b border-border">
      <div className="mf-container">
        {/* Section Header */}
        <Reveal>
          <div className="max-w-3xl mb-14 md:mb-18">
            <Badge variant="outline" className="mb-3 font-mono uppercase tracking-wide mf-caption bg-muted text-foreground">
              {t.badge}
            </Badge>
            <h2 className="mf-h2 text-foreground font-default">
              {t.title}
            </h2>
            <p className="mt-4 mf-lead text-muted-foreground leading-relaxed">
              {t.lead}
            </p>
          </div>
        </Reveal>

        {/* ========================================================================= */}
        {/* (a) Surface filtration vs Depth filtration */}
        {/* ========================================================================= */}
        <div className="mb-20">
          <Reveal>
            <div className="mb-8">
              <h3 className="mf-h3 text-foreground">
                {t.secATitle}
              </h3>
              <p className="mf-caption text-muted-foreground mt-1">
                {t.secADesc}
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
            {/* Surface Filtration Card */}
            <Reveal delay={60}>
              <Card className="h-full border border-border bg-card flex flex-col justify-between">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary">
                      <Maximize2 className="size-5" />
                    </span>
                    <Badge variant="outline" className="mf-caption">
                      {t.surfaceBadge}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl font-bold text-foreground">
                    {t.surfaceTitle}
                  </CardTitle>
                  <CardDescription className="font-semibold text-primary uppercase tracking-wide mf-caption">
                    {t.surfaceSubtitle}
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6 flex-1 flex flex-col justify-between">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {t.surfaceDesc}
                  </p>

                  {/* Technical schematic representation in code tokens */}
                  <div className="rounded-md border border-border bg-muted/40 p-4 space-y-3 font-mono text-muted-foreground mf-caption" aria-hidden="true">
                    <div className="flex items-center justify-between border-b border-border/60 pb-2">
                      <span className="font-semibold text-foreground mf-caption">{t.surfaceInterfaceTitle}</span>
                      <span className="text-primary mf-caption">{t.surfaceInterfaceTag}</span>
                    </div>
                    <div className="py-2 flex flex-col items-center justify-center gap-1.5">
                      {/* Simulated Cake Formation */}
                      <div className="w-full flex justify-around gap-1">
                        <div className="size-3 rounded-full bg-chart-1/80" />
                        <div className="size-2.5 rounded-full bg-chart-2/80" />
                        <div className="size-3.5 rounded-full bg-chart-1/80" />
                        <div className="size-2 rounded-full bg-chart-3/80" />
                        <div className="size-3 rounded-full bg-chart-2/80" />
                      </div>
                      {/* Mesh Barrier hairlines */}
                      <div className="w-full h-1.5 bg-primary/80 rounded-sm relative flex justify-between px-2">
                        <div className="w-1 h-full bg-background" />
                        <div className="w-1 h-full bg-background" />
                        <div className="w-1 h-full bg-background" />
                        <div className="w-1 h-full bg-background" />
                      </div>
                      {/* Filtrate side */}
                      <div className="text-muted-foreground text-center pt-1 mf-caption">
                        {t.surfaceCleanDischarge}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2.5 border-t border-border/60 pt-4">
                    {t.surfacePoints.map((pt, idx) => <div key={idx} data-index={idx} className="flex items-start gap-2.5 text-foreground/90 mf-caption">
                        <CircleCheck className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{pt}</span>
                      </div>)}
                  </div>
                </CardContent>
              </Card>
            </Reveal>

            {/* Depth Filtration Card */}
            <Reveal delay={120}>
              <Card className="h-full border border-border bg-card flex flex-col justify-between">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary">
                      <Layers className="size-5" />
                    </span>
                    <Badge variant="outline" className="mf-caption">
                      {t.depthBadge}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl font-bold text-foreground">
                    {t.depthTitle}
                  </CardTitle>
                  <CardDescription className="font-semibold text-primary uppercase tracking-wide mf-caption">
                    {t.depthSubtitle}
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6 flex-1 flex flex-col justify-between">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {t.depthDesc}
                  </p>

                  {/* Technical schematic representation in code tokens */}
                  <div className="rounded-md border border-border bg-muted/40 p-4 space-y-3 font-mono text-muted-foreground mf-caption" aria-hidden="true">
                    <div className="flex items-center justify-between border-b border-border/60 pb-2">
                      <span className="font-semibold text-foreground mf-caption">{t.depthMatrixTitle}</span>
                      <span className="text-primary mf-caption">{t.depthMatrixTag}</span>
                    </div>
                    <div className="py-1 space-y-1">
                      <div className="h-1 bg-border rounded-full flex justify-around">
                        <div className="size-1.5 rounded-full bg-chart-1 -mt-0.5" />
                      </div>
                      <div className="h-2 bg-muted rounded flex items-center justify-center text-muted-foreground mf-caption">
                        {t.depthCoarseLayer}
                      </div>
                      <div className="h-3 bg-muted rounded flex items-center justify-around px-2 text-foreground font-semibold mf-caption border border-border">
                        <span>{t.depthFineCore}</span>
                        <div className="size-2 rounded-full bg-chart-3" />
                      </div>
                      <div className="h-2 bg-muted rounded flex items-center justify-center text-muted-foreground mf-caption">
                        {t.depthSupportLayer}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2.5 border-t border-border/60 pt-4">
                    {t.depthPoints.map((pt, idx) => <div key={idx} data-index={idx} className="flex items-start gap-2.5 text-foreground/90 mf-caption">
                        <CircleCheck className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{pt}</span>
                      </div>)}
                  </div>
                </CardContent>
              </Card>
            </Reveal>
          </div>
        </div>

        <Separator className="my-16" />

        {/* ========================================================================= */}
        {/* (b) Nominal vs Absolute micron rating */}
        {/* ========================================================================= */}
        <div className="mb-20">
          <Reveal>
            <div className="mb-8">
              <h3 className="mf-h3 text-foreground">
                {t.secBTitle}
              </h3>
              <p className="mf-caption text-muted-foreground mt-1">
                {t.secBDesc}
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Nominal Rating */}
            <Reveal delay={60}>
              <div className="p-6 rounded-lg border border-border bg-card/60 relative">
                <div className="flex items-center justify-between pb-3 border-b border-border/60 mb-4">
                  <div className="flex items-center gap-2">
                    <Activity className="size-5 text-muted-foreground" />
                    <h4 className="font-bold text-lg text-foreground">{t.nominalTitle}</h4>
                  </div>
                  <Badge variant="outline" className="font-mono mf-caption bg-muted">
                    {t.nominalRating}
                  </Badge>
                </div>

                <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                  {t.nominalDesc}
                </p>

                <div className="space-y-3">
                  {t.nominalNotes.map((note, idx) => <div key={idx} data-index={idx} className="text-foreground/80 flex items-start gap-2 mf-caption">
                      <span className="font-mono text-primary font-bold">0{idx + 1}.</span>
                      <span>{note}</span>
                    </div>)}
                </div>
              </div>
            </Reveal>

            {/* Absolute Rating */}
            <Reveal delay={120}>
              <div className="p-6 rounded-lg border border-primary/40 bg-card relative">
                <div className="flex items-center justify-between pb-3 border-b border-border/60 mb-4">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="size-5 text-primary" />
                    <h4 className="font-bold text-lg text-foreground">{t.absoluteTitle}</h4>
                  </div>
                  <Badge className="font-mono bg-primary text-primary-foreground mf-caption">
                    {t.absoluteRating}
                  </Badge>
                </div>

                <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                  {t.absoluteDesc}
                </p>

                <div className="space-y-3">
                  {t.absoluteNotes.map((note, idx) => <div key={idx} data-index={idx} className="text-foreground/90 flex items-start gap-2 mf-caption">
                      <span className="font-mono text-primary font-bold">0{idx + 1}.</span>
                      <span>{note}</span>
                    </div>)}
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        <Separator className="my-16" />

        {/* ========================================================================= */}
        {/* (c) Common weave types overview */}
        {/* ========================================================================= */}
        <div className="mb-20">
          <Reveal>
            <div className="mb-8">
              <h3 className="mf-h3 text-foreground">
                {t.secCTitle}
              </h3>
              <p className="mf-caption text-muted-foreground mt-1">
                {t.secCDesc}
              </p>
            </div>
          </Reveal>

          <Reveal delay={60}>
            <div className="border border-border rounded-lg bg-card overflow-hidden">
              <Accordion type="single" collapsible defaultValue="plain-weave">
                {t.weaves.map((item, idx) => <AccordionItem key={item.id} value={item.id} data-index={idx} className="px-6 py-1">
                    <AccordionTrigger className="hover:no-underline py-4">
                      <div className="flex items-center gap-3 text-left">
                        <span className="font-mono text-muted-foreground mf-caption">0{idx + 1}</span>
                        <span className="font-semibold text-foreground text-base">
                          {item.name}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-5 text-sm space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 bg-muted/40 p-4 rounded-lg border border-border/50">
                        <div className="md:col-span-6 space-y-1">
                          <span className="font-semibold text-primary uppercase tracking-wider block mf-caption">
                            {t.weaveArchitectureLabel}
                          </span>
                          <p className="text-muted-foreground leading-relaxed mf-caption">
                            {item.structure}
                          </p>
                        </div>
                        <div className="md:col-span-6 space-y-1">
                          <span className="font-semibold text-primary uppercase tracking-wider block mf-caption">
                            {t.weaveApplicationsLabel}
                          </span>
                          <p className="text-muted-foreground leading-relaxed mf-caption">
                            {item.use}
                          </p>
                        </div>
                      </div>

                      {item.link && <div className="pt-1 flex items-center justify-end">
                          <Link to={item.link} className="inline-flex items-center font-medium text-primary hover:underline gap-1 mf-caption">
                            <span>{t.weaveSpecLinkText}</span>
                            <ArrowRight className="size-3.5" />
                          </Link>
                        </div>}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>
          </Reveal>
        </div>

        <Separator className="my-16" />

        {/* ========================================================================= */}
        {/* (d) Glossary block defining core terms */}
        {/* ========================================================================= */}
        <div>
          <Reveal>
            <div className="mb-8">
              <h3 className="mf-h3 text-foreground">
                {t.secDTitle}
              </h3>
              <p className="mf-caption text-muted-foreground mt-1">
                {t.secDDesc}
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.glossary.map((entry, idx) => <Reveal key={idx} delay={Math.min(idx * 60, 300)}>
                <Card data-index={idx} className="h-full border border-border bg-card flex flex-col justify-between">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-2">
                      <CardTitle className="text-base font-bold text-foreground">
                        {entry.term}
                      </CardTitle>
                      {entry.symbol && <Badge variant="outline" className="font-mono mf-caption">
                          {entry.symbol}
                        </Badge>}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3 flex-1 flex flex-col justify-between mf-caption">
                    <p className="text-muted-foreground leading-relaxed">
                      {entry.definition}
                    </p>
                    <div className="pt-2 border-t border-border/60">
                      <span className="font-semibold text-foreground/80 block mb-0.5 mf-caption">
                        {t.glossaryRelevanceLabel}
                      </span>
                      <p className="text-muted-foreground/90">
                        {entry.significance}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </Reveal>)}
          </div>
        </div>
      </div>
    </section>;
}